import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { channelPartnerAuthAPI } from '../../services/api';
import { useChannelPartnerAuth } from '../../contexts/ChannelPartnerAuthContext';
import { Card, Button } from '../../components/UI';
import PasswordInput from '../../components/SuperAdmin/PasswordInput';

const PartnerSettings = () => {
  const { partner, refreshProfile } = useChannelPartnerAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Payout details — the partner sets these themselves (only they know their
  // own account); autopay on/off stays a superadmin-only decision, this only
  // controls WHERE the money goes.
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutAccountHolderName, setPayoutAccountHolderName] = useState('');
  const [payoutAccountNumber, setPayoutAccountNumber] = useState('');
  const [payoutIfsc, setPayoutIfsc] = useState('');
  const [payoutVpa, setPayoutVpa] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);

  useEffect(() => {
    if (!partner?.payout) return;
    setPayoutMethod(partner.payout.method || '');
    setPayoutAccountHolderName(partner.payout.accountHolderName || '');
    setPayoutAccountNumber(partner.payout.accountNumber || '');
    setPayoutIfsc(partner.payout.ifsc || '');
    setPayoutVpa(partner.payout.vpa || '');
  }, [partner?.payout]);

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();
    setPayoutError('');

    if (!payoutMethod) {
      setPayoutError('Select a payout method');
      return;
    }
    if (payoutMethod === 'bank' && (!payoutAccountHolderName.trim() || !payoutAccountNumber.trim() || !payoutIfsc.trim())) {
      setPayoutError('Account holder name, account number, and IFSC are required for bank payouts');
      return;
    }
    if (payoutMethod === 'vpa' && !payoutVpa.trim()) {
      setPayoutError('UPI ID is required for UPI payouts');
      return;
    }

    setPayoutSaving(true);
    try {
      const response = await channelPartnerAuthAPI.updatePayout({
        method: payoutMethod,
        accountHolderName: payoutAccountHolderName.trim(),
        accountNumber: payoutAccountNumber.trim(),
        ifsc: payoutIfsc.trim(),
        vpa: payoutVpa.trim(),
      });
      if (response.data?.success) {
        toast.success('Payout details saved');
        refreshProfile();
      } else {
        setPayoutError(response.data?.message || 'Failed to save payout details');
      }
    } catch (err) {
      setPayoutError(err.response?.data?.message || 'Failed to save payout details');
    } finally {
      setPayoutSaving(false);
    }
  };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await channelPartnerAuthAPI.changePassword({ currentPassword, newPassword });
      if (response.data?.success) {
        toast.success('Password changed successfully');
        resetForm();
      } else {
        setError(response.data?.message || 'Failed to change password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!partner) return null;

  return (
    <div>
      <div className="max-w-2xl mx-auto text-center mb-5">
        <h1 className="text-xl lg:text-2xl font-bold text-secondary-900">Settings</h1>
        <p className="text-sm text-secondary-500 mt-0.5">Manage your account security</p>
      </div>

      <div className="space-y-4 max-w-2xl mx-auto">
        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3 text-center">Account Details</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-secondary-100">
              <span className="text-secondary-500">Name</span>
              <span className="text-secondary-900 font-medium">{partner.name}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-secondary-100">
              <span className="text-secondary-500">Email</span>
              <span className="text-secondary-900 font-medium">{partner.email || '—'}</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-secondary-500">Referral Code</span>
              <span className="font-mono font-bold text-primary-600">{partner.token}</span>
            </div>
          </div>
        </Card>

        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-1 text-center">Payout Details</p>
          <p className="text-xs text-secondary-400 text-center mb-4">
            Where we send your commission once it's approved for payout.
          </p>
          <form onSubmit={handlePayoutSubmit} className="space-y-4">
            {payoutError && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded-lg">
                {payoutError}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="payoutMethod" className="block text-sm font-semibold text-secondary-700">Payout Method</label>
              <select
                id="payoutMethod"
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value)}
                disabled={payoutSaving}
                className="w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none"
              >
                <option value="" disabled>Select a method</option>
                <option value="bank">Bank Account</option>
                <option value="vpa">UPI</option>
              </select>
            </div>

            {payoutMethod === 'bank' && (
              <>
                <div className="space-y-1.5">
                  <label htmlFor="accountHolderName" className="block text-sm font-semibold text-secondary-700">Account Holder Name</label>
                  <input
                    id="accountHolderName"
                    type="text"
                    value={payoutAccountHolderName}
                    onChange={(e) => setPayoutAccountHolderName(e.target.value)}
                    placeholder="As per bank records"
                    disabled={payoutSaving}
                    className="w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="accountNumber" className="block text-sm font-semibold text-secondary-700">Account Number</label>
                  <input
                    id="accountNumber"
                    type="text"
                    value={payoutAccountNumber}
                    onChange={(e) => setPayoutAccountNumber(e.target.value)}
                    placeholder="Bank account number"
                    disabled={payoutSaving}
                    className="w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="ifsc" className="block text-sm font-semibold text-secondary-700">IFSC Code</label>
                  <input
                    id="ifsc"
                    type="text"
                    value={payoutIfsc}
                    onChange={(e) => setPayoutIfsc(e.target.value.toUpperCase())}
                    placeholder="e.g. HDFC0001234"
                    disabled={payoutSaving}
                    className="w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none"
                  />
                </div>
              </>
            )}

            {payoutMethod === 'vpa' && (
              <div className="space-y-1.5">
                <label htmlFor="vpa" className="block text-sm font-semibold text-secondary-700">UPI ID</label>
                <input
                  id="vpa"
                  type="text"
                  value={payoutVpa}
                  onChange={(e) => setPayoutVpa(e.target.value)}
                  placeholder="you@upi"
                  disabled={payoutSaving}
                  className="w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none"
                />
              </div>
            )}

            <Button type="submit" variant="primary" loading={payoutSaving} disabled={payoutSaving}>
              Save Payout Details
            </Button>
          </form>
        </Card>

        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-4 text-center">Change Password</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="currentPassword" className="block text-sm font-semibold text-secondary-700">Current Password</label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                autoComplete="current-password"
                required
                disabled={loading}
                style={{ padding: '0.625rem 0.875rem', borderRadius: '0.75rem', border: '2px solid #e2e8f0' }}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="newPassword" className="block text-sm font-semibold text-secondary-700">New Password</label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                required
                disabled={loading}
                style={{ padding: '0.625rem 0.875rem', borderRadius: '0.75rem', border: '2px solid #e2e8f0' }}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-secondary-700">Confirm New Password</label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter the new password"
                autoComplete="new-password"
                required
                disabled={loading}
                style={{ padding: '0.625rem 0.875rem', borderRadius: '0.75rem', border: '2px solid #e2e8f0' }}
              />
            </div>

            <Button type="submit" variant="primary" loading={loading} disabled={loading}>
              Change Password
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PartnerSettings;
